// Снимок страницы: сериализуем ФАКТИЧЕСКИЙ DOM (то, что реально на экране — со
// всеми суммами, именем клиента, применённым тарифом) и отдаём на сервер, где
// Puppeteer отрисовывает его настоящим Chrome в 1984×1116 (16:9) и возвращает PNG.
//
// Почему именно так:
//  • Снимаем живой DOM → в кадре ровно то, что видит пользователь (данные из
//    React-памяти, которых нет в localStorage, сохраняются как есть).
//  • Рендер делает настоящий Chrome → нет артефактов html2canvas: тикер,
//    backdrop-filter, шрифты, графики, внешние картинки — всё как в браузере.
//  • Скрипты из снимка вырезаем, чтобы приложение не перезапустилось в headless
//    и не сбросило состояние в дефолт.

// 1984×1116 — ровно 16:9 (1984/16×9 = 1116), чуть крупнее FullHD.
const OUT_WIDTH = 1984;
const OUT_HEIGHT = 1116;
const ENDPOINT = '/api/screenshot';

// Переносим содержимое <canvas> (графики — lightweight-charts/chart.js) в <img>:
// при клонировании DOM пиксели canvas теряются, поэтому снимаем их с ЖИВЫХ
// элементов (по индексу — порядок в клоне идентичен оригиналу до любых удалений).
function inlineCanvases(cloneRoot) {
    const liveCanvases = document.querySelectorAll('canvas');
    const cloneCanvases = cloneRoot.querySelectorAll('canvas');
    cloneCanvases.forEach((node, i) => {
        const live = liveCanvases[i];
        if (!live) return;
        let data = null;
        try { data = live.toDataURL('image/png'); } catch (_) { return; } // tainted — оставляем
        const img = cloneRoot.ownerDocument.createElement('img');
        img.src = data;
        const styleAttr = node.getAttribute('style');
        if (styleAttr) img.setAttribute('style', styleAttr);
        if (node.className) img.className = node.className;
        node.replaceWith(img);
    });
}

function serializeDom() {
    const docEl = document.documentElement.cloneNode(true);

    // Графики → картинки (до любых удалений, чтобы индексы совпадали).
    inlineCanvases(docEl);

    // <base>, чтобы относительные пути (шрифты, /data/... картинки, /static/css
    // в проде) резолвились от нашего origin.
    const head = docEl.querySelector('head');
    if (head) {
        const base = docEl.ownerDocument.createElement('base');
        base.setAttribute('href', `${window.location.origin}/`);
        head.insertBefore(base, head.firstChild);
    }

    // loading="lazy" в headless никогда не грузится (страницу никто не скроллит),
    // из-за чего сервер зря ждал таймаут ожидания картинок. Делаем все eager.
    docEl.querySelectorAll('img[loading]').forEach((n) => n.setAttribute('loading', 'eager'));

    // Вырезаем скрипты — иначе приложение перезапустится и обнулит состояние.
    docEl.querySelectorAll('script').forEach((n) => n.remove());
    docEl.querySelectorAll('link[rel="preload"][as="script"], link[rel="modulepreload"]').forEach((n) => n.remove());

    // Служебные оверлеи вон из кадра: тост, мини-фигма, тултип, сама админка.
    docEl
        .querySelectorAll('[data-capture-exclude], [data-sonner-toaster], [class*="adminOverlay"]')
        .forEach((n) => n.remove());

    return `<!DOCTYPE html>\n${docEl.outerHTML}`;
}

async function fetchScreenshotBlob({ width, height }) {
    const token = (() => { try { return localStorage.getItem('authToken'); } catch (_) { return null; } })();
    const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            html: serializeDom(),
            viewportWidth: Math.round(window.innerWidth),
            viewportHeight: Math.round(window.innerHeight),
            width,
            height,
        }),
    });
    if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail || ''; } catch (_) { /* noop */ }
        throw new Error(`screenshot ${res.status}${detail ? `: ${detail}` : ''}`);
    }
    return res.blob();
}

export async function capturePageToClipboard(targetEl, opts = {}) {
    const width = opts.width || OUT_WIDTH;
    const height = opts.height || OUT_HEIGHT;

    const makeBlob = () => fetchScreenshotBlob({ width, height });

    if (navigator.clipboard && window.ClipboardItem) {
        try {
            await navigator.clipboard.write([
                new window.ClipboardItem({ 'image/png': makeBlob() }),
            ]);
            return { ok: true, method: 'clipboard' };
        } catch (err) {
            const blob = await makeBlob();
            downloadBlob(blob);
            return { ok: true, method: 'download', error: err };
        }
    }

    const blob = await makeBlob();
    downloadBlob(blob);
    return { ok: true, method: 'download' };
}

function downloadBlob(blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `screenshot-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
